import {
  ActionType,
  FullAction,
  NEW_ACTION_ID,
} from '@groove/api/gateway/v1/actionCompose';

import { SourceType } from './types';

export const SETTINGS_URL = `${process.env.WEB_APPLICATION_BASE_URL}/settings?optionsGroup=scheduler`;
export const LINKEDIN_ACTIONS_URL =
  'https://www.linkedin.com/sales/widget/profile-matching';
export const GMAIL_EMAIL_HEADER_CLASS = '.aeH';
export const APP_MODULE_ROOT_CLASS = 'div[class^="webapp-ModuleRoot-root"]';
export const COMPOSE_PANE_IFRAME_NAME = 'febes-action-compose';
export const COMPOSE_PANE_IFRAME_NATIVE_NAME = 'actionCompose';
export const LIGHT_GREY = 'rgba(0, 0, 0, 0.12)';
export const GROOVE_IFRAME_ROOT = 'groove-actions-iframe-root';
export const TASK_PANE_LOCAL_KEY = 'actionComposeTaskPane';
export const TYPES: { [key: string]: ActionType } = {
  EMAIL: 'TEMPLATE',
  CALL: 'CALL',
  IN_MAIL: 'STEP_SUBTYPE_LINKEDIN_INMAIL',
  LINKEDIN_CONNECT: 'STEP_SUBTYPE_LINKEDIN_CONNECT',
  SENDOSO: 'STEP_SUBTYPE_SENDOSO',
  SMS: 'STEP_SUBTYPE_SMS',
  IN_PERSON_VISIT: 'STEP_SUBTYPE_IN_PERSON_VISIT',
  DIRECT_MAIL: 'STEP_SUBTYPE_DIRECT_MAIL',
  OTHER: 'OTHER',
};
export const SOURCE_TYPE: { [key in SourceType]: SourceType } = {
  FLOW: 'FLOW',
  WORKSPACE: 'WORKSPACE',
  ONE_OFF: 'ONE_OFF',
  PLAY: 'PLAY',
  SUGGESTED_ACTION: 'SUGGESTED_ACTION',
};

type ActionTyping = {
  value: ActionType;
  label: string;
};

export type ActionTypings = ActionTyping[];

export const ACTION_TYPES: ActionTypings = [
  {
    value: 'GENERAL',
    label: 'Task',
  },
  {
    value: 'TEMPLATE',
    label: 'Email',
  },
  {
    value: 'CALL',
    label: 'Call',
  },
  {
    value: 'STEP_SUBTYPE_SMS',
    label: 'SMS',
  },
  {
    value: 'STEP_SUBTYPE_LINKEDIN_CONNECT',
    label: 'LinkedIn Connect',
  },
  {
    value: 'STEP_SUBTYPE_LINKEDIN_INMAIL',
    label: 'LinkedIn InMail',
  },
  {
    value: 'STEP_SUBTYPE_SENDOSO',
    label: 'Sendoso',
  },
];

export type ActionHash = { [key in ActionType]: string };

export const ACTION_HASH: ActionHash = {
  TEMPLATE: 'Email Action',
  CALL: 'Call Action',
  STEP_SUBTYPE_LINKEDIN_INMAIL: 'LinkedIn InMail',
  STEP_SUBTYPE_LINKEDIN_CONNECT: 'LinkedIn Connect',
  STEP_SUBTYPE_SENDOSO: 'Sendoso Action',
  STEP_SUBTYPE_SMS: 'SMS Message',
  STEP_SUBTYPE_IN_PERSON_VISIT: 'In Person Visit',
  STEP_SUBTYPE_DIRECT_MAIL: 'Direct Mail',
  OTHER: 'Other Action',
  GENERAL: 'General Task',
  WORKSPACE_ACCOUNT_TASK: 'Workspace Task',
  UPDATE_SALESFORCE: 'Update Salesforce Values',
};

export const emptyAction: FullAction = {
  id: NEW_ACTION_ID,
  type: 'GENERAL',
  accountId: null,
  accountName: null,
  assignee: null,
  bccRecipients: null,
  body: null,
  ccRecipients: null,
  creatorId: null,
  crmResults: null,
  crmInstanceUrl: null,
  crmObjectId: null,
  crmTaskType: null,
  description: null,
  dueAt: null,
  dynamicData: {
    loggingForm: { title: null, subtitle: null, fields: [] },
  },
  executeAt: null,
  flow: {
    id: null,
    name: null,
  },
  fromEmail: null,
  loggingTo: {},
  notes: null,
  personId: null,
  personStepId: null,
  playNodeRunId: null,
  priority: null,
  results: null,
  subject: null,
  suggestedAction: null,
  summary: null,
  template: null,
  toRecipients: null,
  usedAdvanceSS: false,
  who: null,
  workspaceAccountTaskId: null,
  workspaceSObjectTaskId: null,
};

export const INCOMING_MESSAGE_TYPES = {
  LOAD_ACTION_DATA: 'INCOMING_ACTION_COMPOSE/LOAD_ACTION_DATA',
  UPDATE_ACTION_RECORD: 'INCOMING_ACTION_COMPOSE/UPDATE_ACTION_RECORD',
};

export type FieldsPivotItemValues = 'standard-fields' | 'custom-fields';
export const fieldsPivotItems = [
  { label: 'Standard fields', value: 'standard-fields' },
  { label: 'Custom fields', value: 'custom-fields' },
];

export const MARK_AS_COMPLETE_TOOLTIP_TEXT =
  'Marks action as complete without logging to Salesforce';

export const EMAIL_GENERATION_TYPES = [
  {
    value: 'COLD_EMAIL',
    label: 'Cold email',
    description:
      'Generate a first touch email that includes insights from the contact and account.',
  },
  {
    value: 'FOLLOWUP_EMAIL',
    label: 'Meeting follow-up',
    description:
      'Generate a follow-up email that includes insights from the selected Copilot meeting.',
  },
];
export const EMAIL_WRITING_STYLES = [
  {
    value: 'challenger_sale',
    key: 'challenger_sale',
    text: 'Challenger Sale',
    secondaryText: ['For a conversational and insightful approach'],
    hideTooltip: true,
  },
  {
    value: 'clever_poet',
    key: 'clever_poet',
    text: 'Clever Poet',
    secondaryText: ['To craft content with a creative, poetic twist'],
    hideTooltip: true,
  },
  {
    value: 'cxo_pitch',
    key: 'cxo_pitch',
    text: 'Cxo Pitch',
    secondaryText: ['For concise, executive-level pitches'],
    hideTooltip: true,
  },
  {
    value: 'data_driven',
    key: 'data_driven',
    text: 'Data Driven',
    secondaryText: ['For content backed by data and statistics'],
    hideTooltip: true,
  },
];

export const CONTENT_TYPES = [
  {
    value: 'full_emails',
    key: 'full_emails',
    text: 'Full Email',
    secondaryText: ['Generates a full personalized email'],
    metaData: {
      description:
        'Generates a full personalized email, from the greeting to call-to-action',
    },
    hideTooltip: true,
  },
  {
    value: 'openers',
    key: 'openers',
    text: 'Email Introduction',
    secondaryText: ['Generates a personalized email intrdouction'],
    metaData: {
      description:
        'Generates a personalized email intrdouction, containing the greeting and hook',
    },
    hideTooltip: true,
  },
];

export const CALL_PROCESSED = 'POST_PROCESSING_DONE';
export const RECIPIENT_NOT_FOUND =
  'Recipient not found in Salesforce, use Omnibar to create a new record';
export const RECIPIENT_NOT_FOUND_MODAL_TEXT =
  'We couldn’t find a matching Salesforce record for one or more of the recipients you’ve entered. As a result, sending this email will not be logged for those recipients not in Salesforce. Consider creating a Salesforce record for the indicated recipients before sending.';
